import { useState, useEffect } from 'react'
import { SubmitHandler } from 'react-hook-form'
import 'react-datepicker/dist/react-datepicker.css'
import { useNavigate, useParams } from 'react-router-dom'
import dayjs from '../../utils/DayjsUtil'
import {
  useActualizarAutorizacionMutation,
  useGuardarAutorizacionMutation,
  useLazyRecuperarContactosSitioQuery,
  useRecuperarAutorizacionPorIdQuery,
} from '../../redux/api'
import { AppConstantes } from '../../utils/Constantes'
import toast, { Toaster } from 'react-hot-toast'
import { TAutorizacionForm, useAutorizacionForm } from './models/autorizacionForm.type'
import { style } from '../recordatorios/models/recordatorioForm.type'
import Swal from 'sweetalert2'
import { Recordatorios } from '../recordatorios/Recordatorios'
import { SitioForm } from './components/formAgregarEditarAutorizacion/SitioForm'
import { DatosAutorizacionForm } from './components/formAgregarEditarAutorizacion/DatosAutorizacionForm'
import { ReportesCumplimiento } from './components/reportes/ReportesCumplimiento'
import { FileDropZone } from '../../components/forms/dropzone/fileDropZone.type'
import {
  useGuardarReportesMutation,
  useRecuperarReportesPorIdRelacionadoMutation,
} from '../../redux/api/reportesCumplimiento.api'
import { useDispatch, useSelector } from 'react-redux'
import { cargarReportes } from '../../redux/slices/reportes'
import { Cargando } from '../../components/layout/Cargando'
import ComponentePrivado from '../../components/roles/ComponentePrivado'
import { Roles } from '../../utils/Roles'
import useHasPermiso from '../../hooks/autenticacion/useHasPermiso'
import { RootState } from '../../redux'

import { ContactoSitio } from '../sitios/models'
import { EncabezadoAutorizacionForm } from './components/formAgregarEditarAutorizacion/EncabezadoAutorizacionForm'
import { Reporte } from '../../models'
import { Recordatorio } from '../recordatorios/models'

export const AgregarEditarAutorizacion = () => {
  const { hasPermiso } = useHasPermiso()
  // Permiso de crear tambien gobierna edicion en este flujo (overlay de solo-lectura).
  const usuarioPermiso = hasPermiso([Roles.roleAutorizacionesCrear])

  const navigate = useNavigate()

  const { idAutorizacion } = useParams()

  const isModoEditar = !!idAutorizacion

  // Pantalla de alta/edicion de autorizaciones.
  // Flujo:
  // 1) Guardar/actualizar autorizacion (objeto plano esperado por backend)
  // 2) Subir evidencia documental (FormData) si hay archivos nuevos
  // Nota: si falla la evidencia, la autorizacion puede quedar guardada/actualizada.

  const dispatch = useDispatch()

  const [arrArchivos, setArrArchivos] = useState([] as FileDropZone[])
  const [autorizacionCargada, setAutorizacionCargada] = useState(false)

  const [totalRecordatorios, setTotalRecordatorios] = useState(0)
  const [contactos, setContactos] =
    useState<{ etiqueta: string; valor?: string; correo?: string; descPosicion?: string }[]>()

  const [isAutorizacionIndefinida, setAutorizacionIndefinida] = useState<boolean>()
  const [isPorFechaVencimiento, setPorFechaVencimiento] = useState<boolean>()

  const formAutorizacion = useAutorizacionForm()

  const recuperaReportes = useSelector((state: RootState) => state.reportes)
  const tamanioArrayReportes = recuperaReportes.reportes.length

  const {
    handleSubmit,
    formState: { errors, isDirty, touchedFields },
    control,
    reset,
    getValues,
    register,
    watch,
    trigger,
  } = formAutorizacion

  // Recupera autorizacion por ID. En modo crear se omite el query.
  const {
    data: autorizacion,
    isLoading,
    isError,
    isSuccess,
    error,
    refetch,
  } = useRecuperarAutorizacionPorIdQuery(idAutorizacion ?? '', {
    skip: !isModoEditar,
  })

  // Mutacion: alta de autorizacion.
  const [
    guardarAutorizacion,
    {
      data,
      isLoading: isLoadingGuardar,
      error: errorGuardar,
      isSuccess: isSuccessGuardar,
      isError: isErrorGuardar,
      reset: resetGuardar,
    },
  ] = useGuardarAutorizacionMutation()

  // Mutacion: actualizacion de autorizacion.
  const [
    actualizarAutorizacion,
    {
      isLoading: isLoadingActualizar,
      error: errorAcualizar,
      isSuccess: isSuccessActualizar,
      isError: isErrorActualizar,
    },
  ] = useActualizarAutorizacionMutation()

  const [guardarReportes, { isLoading: isloadingGuardarReportes }] = useGuardarReportesMutation()
  const [recuperarReportes] = useRecuperarReportesPorIdRelacionadoMutation()
  const [recuperarContactos] = useLazyRecuperarContactosSitioQuery()

  /**
   * @description Muesta el swalFire para diferentes casos de éxito o error
   * al guardar o actualizar una autorización
   *
   * @author ana.carrasco Hábil MX
   * @version 1.0.0, 13/01/2023
   * @param tipo Tipo de error: ExitoGuardarAutorizacion o ErrorGuardarActualizarAutorizacion
   * @param mensaje Mensaje que aparece en el cuerpo del swalFire
   * @param isErrorGuardarReportes Verdadero en caso de que hay un error al guardar reporte, y
   * falso cuando se guarden con exito.
   * @param err Mensaje de error al guardar o actualizar una autorizacion
   */
  const abrirSwalFire = (tipo: string, mensaje: string, isErrorGuardarReportes: boolean) => {
    if (tipo === AppConstantes.DESC_EXITOGUARDAR_AUTORIZACION) {
      Swal.fire({
        title: '¡Autorización guardada!',
        icon: 'success',
        text: mensaje,
        showCancelButton: true,
        confirmButtonText: 'Seguir editando',
        cancelButtonText: 'Regresar a autorización',
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then((result) => {
        if (result.isConfirmed) {
          resetGuardar()
          if (!isErrorGuardarReportes) {
            setArrArchivos([])
          }
          navigate(`/autorizaciones/editar/${data.idAutorizacion}`)
        } else {
          navigate('/autorizaciones')
        }
      })
    }
    if (tipo === AppConstantes.DESC_ERRORGUARDARACTUALIZAR_AUTORIZACION) {
      const err: any = isModoEditar ? errorAcualizar : errorGuardar
      Swal.fire({
        title: 'Algo salió mal',
        icon: 'error',
        html: `Por favor valida la información o intentalo mas tarde <br/><p class='mt-5 fs-7'>(${err.status}) ${mensaje}</p>`,
        showCancelButton: true,
        confirmButtonText: 'Intentar de nuevo',
        cancelButtonText: 'Cancelar',
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then((result) => {
        if (result.isConfirmed) {
          handleSubmit(onSubmitt)()
        }
      })
    }
  }

  /**
   * @description Crea objeto para guardar archivo de reporte
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 22/05/2023
   * @param formData Nuevo objeto para asignarle clave-valor.
   * @param idRelacionado id de la autorizacion, tarea o condicionante en la que se guarda el reporte
   */
  const crearObjetoFormData = (formData: FormData, idRelacionado: string) => {
    formData.append('id_relacionado', idRelacionado)
    formData.append('tipo', AppConstantes.CVE_REPORTE_TIPO_AUTORIZACION)
    formData.append('desc_tipo', AppConstantes.DESC_REPORTE_TIPO_AUTORIZACION)
  }

  /**
   * @description Función que recorre el array de archivos cargados.
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 22/05/2023
   * @param formData Nuevo objeto para asignarle clave-valor.
   */
  const recorrerArregloDeArchivosCargados = (formData: FormData) => {
    // Solo adjunta archivos nuevos; los existentes viven en backend y no se re-envian.
    arrArchivos.forEach((a) => {
      if (a.isNew && a.file) {
        formData.append('archivos', a.file, a.file.name)
      }
    })
  }

  /**
   * @description Genera el mensaje para el swalFire cuando ocurre un error
   *              al guardar o actualizar la autorización o sus reportes.
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 22/05/2023
   */
  const generarMensajeErrorActualizarGuardar = () => {
    const err: any = isModoEditar ? errorAcualizar : errorGuardar
    const mensaje = err.data ? err.data.moreInfo : err.error ? err.error : ''
    abrirSwalFire(AppConstantes.DESC_ERRORGUARDARACTUALIZAR_AUTORIZACION, mensaje, false)
  }

  /**
   * @description Crea el objeto con los reportes de una autorizacion guardada.
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 22/05/2023
   */
  const actualizarReportesAutorizacion = () => {
    const textoAutorizacionActualizada = '¡Autorización actualizada!'
    const formData = new FormData()
    crearObjetoFormData(formData, idAutorizacion ?? '')
    if (arrArchivos && arrArchivos.length > 0) {
      recorrerArregloDeArchivosCargados(formData)
      guardarReportes(formData)
        .unwrap()
        .then(() => {
          recuperarReportes(idAutorizacion)
            .unwrap()
            .then((resp:Reporte[]) => {
              dispatch(cargarReportes(resp))
              setArrArchivos([])
              toast.success(textoAutorizacionActualizada, { style })
            })
            .catch(console.error)
        })
        .catch(() => {
          Swal.fire({
            title: textoAutorizacionActualizada,
            icon: 'info',
            html: `Existen algunos reportes sin guardar, verifíquelos y actualice la autorización.`,
            showCancelButton: true,
            confirmButtonText: 'Seguir editando',
            cancelButtonText: 'Cancelar',
            allowOutsideClick: false,
            allowEscapeKey: false,
          }).then((result) => {
            if (result.isConfirmed) {
              resetGuardar()
              navigate(`/autorizaciones/editar/${data.idAutorizacion}`)
            } else {
              navigate('/autorizaciones')
            }
          })
        })
    } else {
      toast.success(textoAutorizacionActualizada, { style })
      refetch()
    }
  }

  /**
   * @description Crea el objeto con los reportes de una autorización nueva.
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 22/05/2023
   */
  const guardarReportesAutorizacion = () => {
    const formData = new FormData()
    formData.append('id_relacionado', data.idAutorizacion)
    formData.append('tipo', AppConstantes.CVE_REPORTE_TIPO_AUTORIZACION)
    formData.append('desc_tipo', AppConstantes.DESC_REPORTE_TIPO_AUTORIZACION)
    if (arrArchivos && arrArchivos.length > 0) {
      recorrerArregloDeArchivosCargados(formData)
      guardarReportes(formData)
        .unwrap()
        .then(() => {
          const mensaje = 'La autorización se ha registrado exitosamente.'
          abrirSwalFire(AppConstantes.DESC_EXITOGUARDAR_AUTORIZACION, mensaje, false)
        })
        .catch(() => {
          if (!isLoadingGuardar || !isLoadingActualizar) {
            const mensaje =
              'La autorización se encuentra registrada, pero los reportes no se pudieron guardar.'
            abrirSwalFire(AppConstantes.DESC_EXITOGUARDAR_AUTORIZACION, mensaje, true)
          }
        })
    } else {
      const mensaje = 'La autorización se encuentra registrada.'
      abrirSwalFire(AppConstantes.DESC_EXITOGUARDAR_AUTORIZACION, mensaje, false)
    }
  }

  useEffect(() => {
    if (isSuccess) {
      recuperarReportes(idAutorizacion)
        .unwrap()
        .then((resp: Reporte[]) => {
          dispatch(cargarReportes(resp))
        })
        .catch(console.error)
    }

    if (isSuccessActualizar) {
      actualizarReportesAutorizacion()
    } else if (!autorizacionCargada && isModoEditar && isSuccess) {
      // Al cargar una autorizacion, se normalizan catálogos y fechas a la forma esperada por UI.
      // Se usa un formato especial para Firefox/Chrome porque el datepicker espera un string ISO.
      reset({
        ...autorizacion,
        autorizacion: {
          valor: autorizacion.autorizacion,
          etiqueta: autorizacion.descAutorizacion,
        },
        numero: autorizacion.numero,
        esIndefinida: autorizacion.esIndefinida,
        esPorFechaVencimiento: autorizacion.esPorFechaVencimiento,

        cantidadDuracion: autorizacion.cantidadDuracion,
        idSitio: {
          valor: autorizacion.idSitio,
          etiqueta: autorizacion.nombreSitio,
        },
        idContactoSitio: {
          valor: autorizacion.idContactoSitio,
          etiqueta: autorizacion.nombreContactoSitio,
        },
        unidadDuracion: {
          valor: autorizacion.unidadDuracion,
          etiqueta: autorizacion.descUnidadDuracion,
        },
        estatus: {
          valor: autorizacion.estatus,
          etiqueta: autorizacion.descEstatus,
        },
        competencia: {
          valor: autorizacion.competencia,
          etiqueta: autorizacion.descCompetencia,
        },
        fechaEmision: dayjs(`${autorizacion.fechaEmision}:00:00:00`).format(
          AppConstantes.FORMATO_FECHA_FIREFOX_CHROME
        ),
        fechaVencimiento: autorizacion.esPorFechaVencimiento
          ? dayjs(`${autorizacion.fechaVencimiento}:00:00:00`).format(
               AppConstantes.FORMATO_FECHA_FIREFOX_CHROME
             )
          : autorizacion.fechaVencimiento,
        fechaUltimaRenovacion: autorizacion.fechaUltimaRenovacion
          ? dayjs(`${autorizacion.fechaUltimaRenovacion}:00:00:00`).format(
              AppConstantes.FORMATO_FECHA_FIREFOX_CHROME
            )
          : null,
        recordatorios: autorizacion.recordatorios?.map((r: Recordatorio) => {
          return {
            ...r,
            cantidad: r.cantidad,
            unidad: {
              etiqueta: r.descUnidad,
              valor: r.unidad,
            },
          }
        }),
      })
      setAutorizacionCargada(true)
      refetch()
    }

    if (isSuccessGuardar) {
      guardarReportesAutorizacion()
    }
    if (isErrorGuardar || isErrorActualizar) {
      generarMensajeErrorActualizarGuardar()
    }

    if (isModoEditar && isSuccess) {
      // Contactos se cargan en base al sitio seleccionado para alimentar el select de contacto.
      recuperarContactos(getValues('idSitio.valor'))
        .unwrap()
        .then((resp: ContactoSitio[]) => {
          const contactos = resp.map((contacto: ContactoSitio) => ({
            etiqueta: ` ${contacto.nombre}  ${contacto.apellidoPaterno}  ${contacto.apellidoMaterno}`,
            valor: contacto.idContacto,
            correo: contacto.correo,
            descPosicion: contacto.descPosicion,
          }))
          setContactos(contactos)
        })
        .catch(console.error)
    } else {
      dispatch(cargarReportes([]))
    }
  }, [
    isModoEditar,
    isSuccess,
    isSuccessGuardar,
    isError,
    isErrorActualizar,
    isSuccessActualizar,
    isErrorGuardar,
  ])

  const onSubmitt: SubmitHandler<TAutorizacionForm> = (data: TAutorizacionForm) => {
    // Mapea valores de formulario (Opcion/OpcionSitio) a shape plano esperado por backend.
    // Tambien aplica reglas de vigencia: indefinida omite duracion y fecha de vencimiento.
    const autorizacion = {
      ...data,
      numero: data.numero,
      autorizacion: data.autorizacion.valor,
      descAutorizacion: data.autorizacion.etiqueta,
      idSitio: data.idSitio.valor,
      nombreSitio: data.idSitio.etiqueta,
      idContactoSitio: data.idContactoSitio?.valor,
      nombreContactoSitio: data.idContactoSitio?.etiqueta,
      esPorFechaVencimiento: data.esPorFechaVencimiento,
      esIndefinida: data.esIndefinida,
      estatus: data.estatus?.valor,
      descEstatus: data.estatus?.etiqueta,
      competencia: data.competencia.valor,
      descCompetencia: data.competencia.etiqueta,
      dependencia: data.dependencia,
      cantidadDuracion: data.esIndefinida ? null : data.cantidadDuracion,
      unidadDuracion: data.esIndefinida ? null : data.unidadDuracion?.valor,
      descUnidadDuracion: data.esIndefinida ? null : data.unidadDuracion?.etiqueta,
      fechaEmision: dayjs(data.fechaEmision).format(AppConstantes.FORMATO_FECHA_POST),
      fechaVencimiento: !data.esIndefinida
        ? dayjs(data.fechaVencimiento).format(AppConstantes.FORMATO_FECHA_POST)
        : null,
      fechaUltimaRenovacion: data.fechaUltimaRenovacion
        ? dayjs(data.fechaUltimaRenovacion).format(AppConstantes.FORMATO_FECHA_POST)
        : null,
    }

    if (!isModoEditar) {
      // En alta, recordatorios se envian anidados en la misma llamada.
      // Recordatorios: para unidades no personalizadas se define `fechaReferencia` para calculo server-side.
      // Si es personalizado, se manda `fechaRecordatorio` como timestamp formateado.
      autorizacion.recordatorios = data.recordatorios?.map((r: any) => {
        return {
          ...r,
          cantidad: r?.cantidad,
          unidad: r.unidad?.valor,
          descUnidad: r.unidad?.etiqueta,
          fechaReferencia:
            r.unidad.valor === AppConstantes.CVE_UNIDAD_RECORDATORIO_PERSONALIZADO
              ? null
              : autorizacion.fechaVencimiento,
          fechaRecordatorio: r.fechaRecordatorio
            ? dayjs(r.fechaRecordatorio).format(AppConstantes.FORMATO_FECHA_RECORDATORIO_POST)
            : null,
        }
      })

      guardarAutorizacion(autorizacion)
    } else {
      // En edicion, los reportes se gestionan por separado (ver `actualizarReportesAutorizacion`).
      actualizarAutorizacion(autorizacion)
      refetch()
    }
  }

  return (
    <>
      <Toaster />
      <div className='container'>
        <div className='row'>
          <EncabezadoAutorizacionForm
            isModoEditar={isModoEditar}
            isSuccess={isSuccess}
            autorizacion={autorizacion}
            idAutorizacion={idAutorizacion}
            isLoading={isLoading}
            isError={isError}
            error={error}
            usuarioPermiso={usuarioPermiso}></EncabezadoAutorizacionForm>

          <div className='d-flex'>
            <div className={`${usuarioPermiso ? '' : 'overlay overlay-allowed'} card `}>
              <div>
                {(!isModoEditar || (isModoEditar && isSuccess)) && (
                  <form onSubmit={handleSubmit(onSubmitt)}>
                    <div
                      className={` row ${
                        isLoadingGuardar || isloadingGuardarReportes || isLoadingActualizar
                          ? 'overlay overlay-block'
                          : ''
                      }`}>
                      <div className='mt-10'>
                        <SitioForm
                          control={control}
                          errors={errors}
                          contactos={contactos}
                          setContactos={setContactos}
                          formPagina={formAutorizacion}
                          usuarioPermiso={usuarioPermiso}></SitioForm>
                      </div>
                      <div className='col-12 mt-10 mb-10 border-bottom'></div>
                      <div className=''>
                        <DatosAutorizacionForm
                          formPagina={formAutorizacion}
                          isAutorizacionIndefinida={isAutorizacionIndefinida}
                          isPorFechaVencimiento={isPorFechaVencimiento}
                          autorizacion={autorizacion}
                          setAutorizacionIndefinida={setAutorizacionIndefinida}
                          setPorFechaVencimiento={setPorFechaVencimiento}
                          isModoEditar={isModoEditar}
                          isSuccess={isSuccess}
                          usuarioPermiso={usuarioPermiso}></DatosAutorizacionForm>
                      </div>
                      <div className='col-12 mt-10 mb-10 border-bottom'></div>
                      <div className='ms-20 d-flex row'>
                        <div className='d-flex'>
                          <div className='col-2'>
                            <h3 className='me-15'>Documentos asociados</h3>
                          </div>

                          <div
                            className={`${
                              usuarioPermiso
                                ? 'col-9 mb-10'
                                : tamanioArrayReportes > 0
                                ? 'col-5 mb-10'
                                : 'col-9'
                            }`}>
                            <ReportesCumplimiento
                              arrArchivos={arrArchivos}
                              setArrArchivos={setArrArchivos}
                              idRelacionado={idAutorizacion ?? ''}
                              height={300}
                              heightMaxScroll={500}
                              width={'100%'}
                              usuarioPermisoCrear={usuarioPermiso}
                              textoTipoArchivo={'No hay documentos asociados'}
                            />
                          </div>
                        </div>
                      </div>
                      <div className='col-12 mb-10 border-bottom'></div>
                      <div className='ms-20 d-flex row'>
                        <div className='d-flex'>
                          <div className='col-2'>
                            <h3>Recordatorios</h3>
                            <span className='text-muted fs-4'> {totalRecordatorios} total</span>
                          </div>
                          <div className='col-9 mb-10 mt-10'>
                            <Recordatorios
                              setTotalRecordatorios={setTotalRecordatorios}
                              tipo={AppConstantes.CVE_RECORDATORIO_AUTORIZACION}
                              esIndefinida={isAutorizacionIndefinida}
                              isModoEditar={isModoEditar}
                              fechaReferencia={autorizacion?.fechaVencimiento}
                              idRelacionado={idAutorizacion}
                              esAtributoAnidado={false}
                              estatus={autorizacion?.estatus}
                              {...{
                                getValues,
                                trigger,
                                watch,
                                control,
                                register,
                                errors,
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      {(isLoadingActualizar || isloadingGuardarReportes || isLoadingGuardar) && (
                        <Cargando mensaje='Guardando autorización'></Cargando>
                      )}

                      <div className='card-footer'>
                        <div className='col-12 d-flex justify-content-end'>
                          <button className='btn me-3' type='button' onClick={() => navigate(-1)}>
                            Cancelar
                          </button>
                          <ComponentePrivado rolMostrarPorPermiso={[Roles.roleAutorizacionesCrear]}>
                            <button
                              className='btn btn-primary p-3'
                              disabled={
                                !isDirty &&
                                arrArchivos.length === 0 &&
                                !touchedFields.esIndefinida &&
                                !touchedFields.esPorFechaVencimiento &&
                                !touchedFields.dependencia &&
                                !touchedFields.numero
                              }
                              type='submit'>
                              {`${!isModoEditar ? 'Guardar' : 'Actualizar'} autorización`}
                            </button>
                          </ComponentePrivado>
                        </div>
                      </div>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
